import MatchDetailsTabModel from '../common/model-match-details-tab';
import MatchDetailsOddModel from '../common/model-match-details-odd';
import MatchDetailsOutcomeModel from '../common/model-match-details-outcome';
import DateFilterService from '../../../../services/date-filter-service';

/**
 * Model for Match Details
 */
export default class MatchDetailsModel {
  constructor(data, avoidDateFilter = false, dateFormat, timeFormat) {
    this.dateFormat = dateFormat || APP_SETTINGS.formatting.fullDateFormat;
    this.timeFormat = timeFormat || APP_SETTINGS.formatting.fullTimeFormat;
    this.avoidDateFilter = avoidDateFilter;
    this.addMetadata(data);
    this.addOdds(data);
  }

  /**
   * Method for mapping inner objects from API with parent model
   * @param oddsList list of odds
   * @return mapped outcomes and odds in proper model
   */
  mapOdds(oddsList) {
    if (!oddsList) {
      return [];
    }
    let odds = [];
    oddsList.forEach((o) => {
      let odd = new MatchDetailsOddModel(o, this.homeTeam, this.guestTeam);
      odd.outcomes = o.m ? o.m.map(m => new MatchDetailsOutcomeModel(m, this.homeTeam, this.guestTeam)) : [];
      odds.push(odd);
    });
    return odds;
  }

  mapTabs(tabsList) {
    if (!tabsList) {
      return [];
    }
    let tabs = [];
    tabsList.forEach((t) => {
      let tab = new MatchDetailsTabModel(t, this.sportId, this.homeTeam, this.guestTeam);
      tabs.push(tab);
    });
    return tabs;
  }

  addMetadata(data) {
    /**
     * Sport name
     * @type {string}
     */
    this.sportName = data.sn || data.s;
    /**
     * Home team name
     * @type {string}
     */
    this.homeTeam = data.ht;
    /**
     * Home team Icon, we get this info from betradar
     * @type {string}
     */
    // eslint-disable-next-line no-undef
    this.homeTeamIcon = data.hti ? `${APP_SETTINGS.path.teamImageUrl}${data.hti}` : '';
    /**
     * Guest team name
     * @type {string}
     */
    this.guestTeam = data.at;
    /**
     * Guest team Icon, we get this info from betradar
     * @type {string}
     */
    // eslint-disable-next-line no-undef
    this.guestTeamIcon = data.ati ? `${APP_SETTINGS.path.teamImageUrl}${data.ati}` : '';
    /**
     * Sport ID
     * @type {number}
     */
    this.sportId = data.sid;
    /**
     * Sport -> Category ID
     * @type {number}
     */
    this.categoryId = data.cid;
    /**
     * Category name
     */
    this.categoryName = data.cn;
    /**
     * Sport -> Category -> Tournament ID
     * @type {number}
     */
    this.tournamentId = data.tid;
    /**
     * Tournament name
     */
    this.tournamentName = data.tn;
    /**
     * Sport -> Category -> Tournament -> Match ID
     * @type {number}
     */
    this.matchId = data.mid;
    /**
     * Match ID from betradar
     * @type {number}
     */
    this.betRadarId = data.brid;
    /**
     * Feed provider code
     * @type {number}
     */
    this.feedProvider = data.fpc;
    /**
     * Match date (converted to local date)
     * @type {string}
     */
    // eslint-disable-next-line no-undef
    if (this.avoidDateFilter) {
      this.date = data.sd;
    } else {
      this.date = data.d ? DateFilterService.localDate(data.d, 'date', this.dateFormat, '') : undefined;
    }
    /**
     * Match time (converted to local time)
     * @type {string}
     */
    // eslint-disable-next-line no-undef
    if (this.avoidDateFilter) {
      this.time = data.mt; // !!! utc
    } else {
      this.time = data.d ? DateFilterService.localDate(data.d, 'time', '', this.timeFormat) : undefined;
    }
    /**
     * Match apiDateTime as we get it from the API
     * @type {string}
     */
    this.apiDateTime = data.d;
    /**
     * Match Odds + Odds->Outcomes
     * @type {array}
     */
    if (!this.odds) {
      this.odds = this.mapOdds(data.o);
    }
    /**
     * Match markets + nested odds, outcomes
     * @type {array}
     */
    if (!this.tabs) {
      this.tabs = this.mapTabs(data.t);
    }
    /**
     * Specail case where we get the odds before the metadata
     * we need to remap the tabs so that we can get the correct
     * enriched markets/outcome names
     */
    if (this.rawTabs) {
      this.tabs = this.mapTabs(this.rawTabs);
      if (this.tabs[0].odds) {
        this.odds = this.tabs[0].odds;
      }
      delete this.rawTabs;
    }
    /**
     * Match number of odds
     * @type {number}
     */
    this.numberOfOdds = data.n;
    /**
     * Match number of markets
     * @type {number}
     */
    this.numberOfMarkets = data.nm;
    /**
     * Event code
     */
    this.eventCode = data.ec;
    /**
     * cash out visibility
     */
    this.cashOutVisibility = data.cs;
    /**
     * Suspended match
     * @type {boolean}
     */
    this.suspended = data.su;
    /**
     * Is tournament US
     * @type {boolean}
     */
    this.awayAtHome = data.tus;
    /**
     * Flag if  match has live stream
     * @type {boolean}
     */
    this.hasLiveStream = data.lc;
    /**
     * Identifies SportCast offer
     * @type {number | null}
     */
    this.sportCastFixtureId = data.scfi;
    /**
     * Sport Cast sport id
     * @type {number | null}
     */
    this.sportCastSportId = data.scsid;
    /**
     * Match name
     * @type {string}
     */
    this.name = this.getName(data);
    /**
     * Identifies SportCast offer
     * @type {number | null}
     */
    this.swishFixtureId = data.sm;
  }

  getName(data) {
    if (data.tus) {
      return `${this.guestTeam} @ ${this.homeTeam}`;
    }
    return `${this.homeTeam} vs ${this.guestTeam}`;
  }

  addOdds(data) {
    /**
     * Match Odds + Odds->Outcomes
     * @type {array}
     */

    /**
     * Match markets + nested odds, outcomes
     * @type {array}
     */
    this.tabs = this.mapTabs(data.t);
    /**
     * If we don't have metadata yet, store raw tabs in rawTabs property
     * So that we can remap once we get the metadata
     */
    if (!this.homeTeam) {
      this.rawTabs = data.t;
    }
    if (this.tabs) {
      let odds = this.tabs[0] !== undefined ? this.tabs[0].odds : this.mapOdds(data.o);
      this.odds = odds;
    }
    /**
     * if matchId is not already returned by metaData get it from here
     */
    this.matchId = this.matchId || data.mid;
  }
}
